import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { skillService } from '../../services/skillService';
import { goalService } from '../../services/goalService';
import { Skill } from '../../types/Skill';
import { Goal } from '../../types/Goal';
import PageHeader from '../common/PageHeader';
import SkillRoadmap from './SkillRoadmap';
import './SkillMap.css';

interface SkillMapProps {
  onToggleSidebar: () => void;
}

const SkillMap: React.FC<SkillMapProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [nonMappingGoals, setNonMappingGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'years' | 'months'>('months');

  useEffect(() => {
    if (user) {
      loadSkills();
    }
  }, [user]);

  const loadSkills = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const result = await skillService.getUserSkills(user.id.toString());
      
      if (result.success && result.skills) {
        // Convert linkedGoals from Firebase format to Goal format
        const convertedSkills: Skill[] = result.skills.map(skill => {
          const convertedLinkedGoals: Goal[] | undefined = skill.linkedGoals && Array.isArray(skill.linkedGoals)
            ? skill.linkedGoals.map((goal: any) => {
                try {
                  return goalService.convertFirebaseGoal(goal);
                } catch (err) {
                  console.warn('Error converting goal:', err, goal);
                  return null;
                }
              }).filter((g): g is Goal => g !== null)
            : undefined;
          
          return {
            ...skill,
            linkedGoals: convertedLinkedGoals
          } as Skill;
        });
        console.debug('Converted skills:', convertedSkills.map(s => ({
          name: s.name,
          linkedGoalsCount: s.linkedGoals?.length || 0,
          linkedGoals: s.linkedGoals?.map((g: any) => ({ title: g.title, targetDate: g.targetDate }))
        })));
        setSkills(convertedSkills);

        // Fetch all goals and filter out goals linked to skills
        const goalsResult = await goalService.getUserGoals({
          user_id: user.id.toString()
        });

        if (goalsResult.success && goalsResult.goals) {
          // Collect all goal IDs that are linked to skills
          const linkedGoalIds = new Set<string>();
          convertedSkills.forEach(skill => {
            if (skill.linkedGoals) {
              skill.linkedGoals.forEach(goal => {
                linkedGoalIds.add(goal.id);
              });
            }
          });

          // Convert Firebase goals to Goal format and filter out linked ones
          const allGoals: Goal[] = goalsResult.goals
            .map((goal: any) => {
              try {
                return goalService.convertFirebaseGoal(goal);
              } catch (err) {
                console.warn('Error converting goal:', err, goal);
                return null;
              }
            })
            .filter((g): g is Goal => g !== null);

          // Filter out goals that are linked to any skill
          const nonMapping = allGoals.filter(goal => !linkedGoalIds.has(goal.id));
          setNonMappingGoals(nonMapping);
          console.debug('Non-mapping goals:', nonMapping.length, nonMapping.map(g => ({ title: g.title, id: g.id })));
        } else {
          setNonMappingGoals([]);
        }
      } else {
        setSkills([]);
        setNonMappingGoals([]);
      }
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load skills');
      console.error('Error loading skills:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = () => {
    navigate('/skillmap/new');
  };

  const handleToggleViewMode = () => {
    setViewMode(prev => prev === 'years' ? 'months' : 'years');
  };

  const rightActions = (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
      <button onClick={handleToggleViewMode} className="skillmap-view-toggle-btn">
        {viewMode === 'years' ? 'Months' : 'Years'}
      </button>
      <button onClick={handleAddSkill} className="skillmap-add-btn">
        + Add Skill
      </button>
    </div>
  );

  if (!user) {
    return (
      <div className="skillmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="skillmap-header-title">/skillmap</h1>}
        />
        <div className="skillmap-auth-required">
          <p>Please sign in to manage your skills.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="skillmap-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="skillmap-header-title">/skillmap</h1>}
        rightActions={rightActions}
      />
      
      <div className="skillmap-content">
        {error && (
          <div className="skillmap-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {loading ? (
          <div className="skillmap-loading">Loading skills...</div>
        ) : skills.length === 0 ? (
          <div className="skillmap-empty-state">
            <h2>No skills yet</h2>
            <p>Start by creating your first skill to track your progress!</p>
            <button onClick={handleAddSkill} className="skillmap-empty-add-btn">
              Create Your First Skill
            </button>
          </div>
        ) : (
          <SkillRoadmap skills={skills} nonMappingGoals={nonMappingGoals} viewMode={viewMode} />
        )}
      </div>
    </div>
  );
};

export default SkillMap;

